/**
 * OpeningBalancesDialog — soldes d'ouverture (une écriture GEN « OUVERTURE », remplacée à chaque enregistrement).
 */
import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { r2 } from "@/lib/format";
import { Btn, Field, Modal, TYPE_LABEL, TYPE_ORDER, errMsg, inputCls, money, useGlBase } from "./_shared";

const DEBIT_NORMAL = new Set(["asset", "expense"]);

export default function OpeningBalancesDialog({ settings, accounts, onClose, onSaved }) {
  const base = useGlBase();
  const [date, setDate] = useState(settings.opening_date || `${new Date().getFullYear()}-01-01`);
  const [bal, setBal] = useState({});
  const [busy, setBusy] = useState(false);
  const eqId = settings.roles?.opening_equity;
  const detail = accounts.filter((a) => a.kind === "A" && a.is_active !== false && a.id !== eqId);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`${base}/entries`, { params: { source_type: "opening", limit: 1 } });
        const e = r.data.items?.[0];
        if (!e) return;
        const byId = Object.fromEntries(accounts.map((a) => [a.id, a]));
        const next = {};
        for (const ln of e.lines) {
          const a = byId[ln.account_id];
          if (!a || a.id === eqId) continue;
          next[ln.account_id] = String(DEBIT_NORMAL.has(a.type) ? r2(ln.debit - ln.credit) : r2(ln.credit - ln.debit));
        }
        setBal(next); setDate(e.date);
      } catch { /* pas d'écriture d'ouverture */ }
    })();
  }, [accounts, eqId, base]);

  let dr = 0, cr = 0;
  for (const a of detail) {
    const v = parseFloat(bal[a.id]); if (!v) continue;
    const d = DEBIT_NORMAL.has(a.type) ? v : -v;
    if (d >= 0) dr += d; else cr += -d;
  }
  const diff = r2(dr - cr);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`${base}/opening-balances`, { date, balances: Object.fromEntries(Object.entries(bal).filter(([, v]) => v !== "" && v != null)) });
      toast.success(r.data.result === "none" ? "Aucun solde d'ouverture" : `Soldes d'ouverture enregistrés (${r.data.nb_lines} ligne(s))`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Soldes d'ouverture" sub="Saisis le solde de chaque compte à la date d'ouverture (selon son côté normal : positif = débiteur pour l'actif et les dépenses, créditeur pour le passif, les capitaux et les revenus). La différence est portée au compte « Solde d'ouverture (contrepartie) »." onClose={onClose} wide testId="opening-dialog"
      footer={<>
        <div className="mr-auto text-xs text-slate-600 tabular-nums" data-testid="opening-totals">Débits {money(dr)} · Crédits {money(cr)} · <strong className={diff ? "text-amber-700" : "text-emerald-700"}>Contrepartie {money(Math.abs(diff))}</strong></div>
        <Btn tone="slate" onClick={onClose} testId="opening-cancel">Annuler</Btn>
        <Btn icon={Save} onClick={save} busy={busy} disabled={!eqId} title={!eqId ? "Définis d'abord le rôle « Solde d'ouverture (contrepartie) »" : ""} testId="opening-save">Enregistrer</Btn>
      </>}>
      {!eqId && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="opening-no-equity">Le rôle « Solde d'ouverture (contrepartie) » n'est pas défini — ouvre « Rôles & réglages » d'abord.</p>}
      <Field label="Date d'ouverture" className="max-w-xs">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="opening-date" />
      </Field>
      {TYPE_ORDER.map((t) => {
        const list = detail.filter((a) => a.type === t);
        if (!list.length) return null;
        return (
          <div key={t}>
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">{TYPE_LABEL[t]}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {list.map((a) => (
                <label key={a.id} className={`block rounded-lg border p-2 ${bal[a.id] ? "border-emerald-400 bg-emerald-50" : "border-slate-200"}`}>
                  <span className="block text-[10px] text-slate-500 leading-tight truncate"><span className="font-mono">{a.number}</span> {a.name}</span>
                  <input type="number" step="0.01" value={bal[a.id] ?? ""} onChange={(e) => setBal({ ...bal, [a.id]: e.target.value })} placeholder="0.00"
                    className="w-full bg-transparent text-sm font-mono outline-none" data-testid={`opening-${a.number}`} />
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </Modal>
  );
}
